import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export interface DropdownProps {
  /** Accessible name for the trigger, e.g. "Feedback for Song A: Liked". */
  label: string;
  /** What the closed control displays. */
  trigger: React.ReactNode;
  triggerClassName?: string;
  /** Accessible name for the open menu. */
  menuLabel: string;
  /** Marks the trigger busy while a change made from the menu is saving. */
  busy?: boolean;
  /**
   * The menu's items. Give each a `menuitem`, `menuitemradio` or
   * `menuitemcheckbox` role so the arrow keys can reach it, and the
   * `.menu-item` class for the shared look. Call `close` to dismiss.
   */
  children: (close: () => void) => React.ReactNode;
}

const GAP = 4;
const VIEWPORT_MARGIN = 8;

function menuItemsIn(menu: HTMLElement | null): HTMLElement[] {
  return Array.from(
    menu?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? []
  );
}

/**
 * A button that opens a small menu.
 *
 * The menu renders into document.body with fixed positioning. Song rows sit
 * inside a scroll container that clips overflow, so a menu positioned inside
 * the row would be cut off on the last few rows. Because it's detached, it
 * closes on any scroll rather than drifting away from its trigger.
 *
 * Keyboard: Arrow Down/Up opens it from the trigger; inside, arrows, Home and
 * End move between items, and Escape or Tab closes it and returns focus.
 */
export function Dropdown(props: DropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const focusedOnOpen = useRef(false);
  const menuId = useId();

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setPosition(null);
    focusedOnOpen.current = false;
    if (restoreFocus) {
      // preventScroll: a scroll would immediately re-trigger the close handler.
      triggerRef.current?.focus({ preventScroll: true });
    }
  }, []);

  // Measure once the menu has rendered (hidden) so its real size is known:
  // below the trigger with right edges aligned, flipped above when it would
  // run off the bottom, and clamped inside the viewport.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) {
      return;
    }
    const trigger = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const fitsBelow =
      trigger.bottom + GAP + menu.height <=
      window.innerHeight - VIEWPORT_MARGIN;
    const fitsAbove = trigger.top - GAP - menu.height >= VIEWPORT_MARGIN;

    setPosition({
      top:
        !fitsBelow && fitsAbove
          ? trigger.top - GAP - menu.height
          : trigger.bottom + GAP,
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          trigger.right - menu.width,
          window.innerWidth - VIEWPORT_MARGIN - menu.width
        )
      ),
    });
  }, [open]);

  // Move focus in once the menu is placed: to the selected item if there is
  // one, so the current value is where the keyboard starts.
  useEffect(() => {
    if (!position || focusedOnOpen.current) {
      return;
    }
    focusedOnOpen.current = true;
    const items = menuItemsIn(menuRef.current);
    (
      items.find((el) => el.getAttribute("aria-checked") === "true") ?? items[0]
    )?.focus({ preventScroll: true });
  }, [position]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function isInside(target: EventTarget | null): boolean {
      return (
        target instanceof Node &&
        (!!menuRef.current?.contains(target) ||
          !!triggerRef.current?.contains(target))
      );
    }
    function onPointerDown(event: Event): void {
      if (!isInside(event.target)) {
        close(false);
      }
    }
    function onScroll(event: Event): void {
      if (!(
        event.target instanceof Node && menuRef.current?.contains(event.target)
      )) {
        close(false);
      }
    }
    function onResize(): void {
      close(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    // Capture: scroll doesn't bubble, and the list scrolls in its own element.
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  function onTriggerKeyDown(event: React.KeyboardEvent): void {
    if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      setOpen(true);
    }
  }

  function onMenuKeyDown(event: React.KeyboardEvent): void {
    const items = menuItemsIn(menuRef.current);
    const current = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (index: number) =>
      items[(index + items.length) % items.length]?.focus({
        preventScroll: true,
      });

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusAt(current + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusAt(current < 0 ? -1 : current - 1);
        break;
      case "Home":
        event.preventDefault();
        focusAt(0);
        break;
      case "End":
        event.preventDefault();
        focusAt(-1);
        break;
      case "Escape":
      case "Tab":
        // preventDefault also tells a surrounding Modal not to close.
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      default:
        break;
    }
  }

  // React bubbles events along the component tree, portals included, so a
  // click on a menu item would otherwise reach whatever contains this — a
  // song row, whose click opens the song.
  return (
    <span className="inline-flex" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={props.label}
        aria-busy={props.busy || undefined}
        className={props.triggerClassName}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onTriggerKeyDown}
      >
        {props.trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={props.menuLabel}
            onKeyDown={onMenuKeyDown}
            className="fixed z-50 min-w-[11rem] py-1 bg-raised border border-rule-strong rounded shadow-2xl"
            style={position ?? { top: 0, left: 0, visibility: "hidden" }}
          >
            {props.children(() => close(true))}
          </div>,
          document.body
        )}
    </span>
  );
}

export default Dropdown;
